/**
 * Sidebar navigation component styles.
 *
 * Provides base structural styles for navigation sidebars.
 * Animation/transition styles are in sidebar-collapse.css.
 *
 * Supports glassmorphism effects for native window vibrancy.
 * Supports collapsed "ribbon" mode with smooth CSS animations.
 */

import type { ReactNode } from "react";
import * as stylex from "@stylexjs/stylex";
import { motion, type Variants } from "framer-motion";
import { useMemo } from "react";

import {
  debugExitPresets,
  debugSpringPresets,
  exitPresets,
  springPresets,
  useAnimation,
} from "../animation";
import {
  colors,
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
  radius,
  space,
} from "../tokens.stylex";

const EXPANDED_WIDTH = 240;

const styles = stylex.create({
  // Container
  container: {
    width: EXPANDED_WIDTH,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    paddingTop: space.md,
    paddingBottom: space.md,
    paddingLeft: space.md,
    paddingRight: space.md,
    overflow: "visible",
  },
  containerSurface: {
    backgroundColor: colors.background,
  },
  containerTransparent: {
    paddingRight: "0px",
  },

  // Header section
  header: {
    marginBottom: space.xl,
    paddingInline: space.xs,
  },

  // User profile section
  userProfile: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    flex: 1,
    minWidth: 0,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: radius.full,
    flexShrink: 0,
    objectFit: "cover",
    backgroundColor: colors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    color: colors.primaryForeground,
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  userName: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: colors.foreground,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userEmail: {
    fontSize: fontSize["2xs"],
    color: colors.foregroundMuted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userRole: {
    fontSize: fontSize["2xs"],
    fontWeight: fontWeight.medium,
    color: colors.accentForeground,
    backgroundColor: colors.accent,
    paddingInline: space.sm,
    paddingBlock: space["2xs"],
    borderRadius: radius.sm,
    alignSelf: "flex-start",
  },

  // Collapse toggle button
  collapseToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: radius.md,
    backgroundColor: "transparent",
    borderWidth: 0,
    cursor: "pointer",
    color: colors.foregroundSubtle,
    flexShrink: 0,
    ":hover": {
      backgroundColor: colors.muted,
      color: colors.foreground,
    },
  },

  // Navigation
  nav: {
    display: "flex",
    flexDirection: "column",
    gap: space["2xs"],
    flex: 1,
  },

  // Navigation link - base structural styles only
  // Note: gap and padding are animated by SidebarLinkMotion, not set here
  link: {
    display: "flex",
    alignItems: "center",
    borderRadius: radius.md,
    color: colors.foregroundMuted,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    letterSpacing: letterSpacing.tight,
    lineHeight: lineHeight.normal,
    textDecoration: "none",
    cursor: "pointer",
    position: "relative",
    borderWidth: 0,
    backgroundColor: "transparent",
    textAlign: "left",
    width: "100%",
    ":hover": {
      backgroundColor: colors.muted,
      color: colors.foreground,
    },
  },
  linkActive: {
    backgroundColor: colors.accent,
    color: colors.accentForeground,
  },
  linkActiveIndicator: {
    "::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "3px",
      height: "16px",
      backgroundColor: colors.primary,
      borderRadius: "0 2px 2px 0",
    },
  },

  // Link label - no opacity/width here, CSS handles it
  linkLabel: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },

  // Section group
  sectionGroup: {
    marginTop: space.lg,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingInline: space.sm,
    paddingBlock: space.xs,
    marginBottom: space.xs,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    color: colors.foregroundSubtle,
    borderRadius: radius.sm,
    cursor: "pointer",
    backgroundColor: "transparent",
    borderWidth: 0,
    width: "100%",
    textAlign: "left",
    overflow: "hidden",
    ":hover": {
      backgroundColor: colors.muted,
    },
  },
  sectionHeaderWrapper: {
    overflow: "hidden",
  },
  sectionLabel: {},
  sectionChevron: {
    transitionProperty: "transform",
    transitionDuration: "250ms",
    transitionTimingFunction: "cubic-bezier(0.33, 1, 0.68, 1)",
  },
  sectionChevronCollapsed: {
    transform: "rotate(-90deg)",
  },
  sectionContentInner: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: space["2xs"],
  },

  // Footer (collapsed settings menu)
  footer: {
    marginTop: "auto",
    paddingTop: space.md,
  },

  // Icon styles - base only, CSS handles size changes
  navIcon: {
    width: "18px",
    height: "18px",
    flexShrink: 0,
    color: colors.foregroundSubtle,
  },
  navIconActive: {
    color: colors.primary,
  },

  // Divider (legacy)
  divider: {
    marginTop: space.xl,
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: colors.borderSubtle,
    paddingTop: space.md,
  },
});

export const sidebar = styles;

export interface SidebarLinkMotionProps {
  isCollapsed: boolean;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  style?: Readonly<Record<string, string | number>>;
  [key: `data-${string}`]: boolean | string | undefined;
}

export function SidebarLinkMotion({
  isCollapsed,
  children,
  className,
  onClick,
  type,
  style,
  ...dataProps
}: SidebarLinkMotionProps) {
  const { getSpring } = useAnimation();
  const transition = getSpring("instant");

  const variants = useMemo(
    (): Variants => ({
      collapsed: {
        height: 36,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 9.5,
        paddingRight: 8,
        transition: {
          height: transition,
          paddingTop: transition,
          paddingBottom: transition,
          paddingLeft: { duration: 0.2, ease: [0.33, 1, 0.68, 1] },
          paddingRight: transition,
        },
      },
      expanded: {
        height: 30,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 8,
        paddingRight: 12,
        transition: {
          height: transition,
          paddingTop: transition,
          paddingBottom: transition,
          paddingLeft: { duration: 0.2, ease: [0.33, 1, 0.68, 1] },
          paddingRight: transition,
        },
      },
    }),
    [transition]
  );

  return (
    <motion.button
      {...dataProps}
      animate={isCollapsed ? "collapsed" : "expanded"}
      className={className}
      initial={false}
      onClick={onClick}
      style={{ ...style, overflow: "hidden" }}
      type={type}
      variants={variants}
    >
      {children}
    </motion.button>
  );
}

export interface SidebarSectionHeaderMotionProps {
  isCollapsed: boolean;
  children: ReactNode;
  className?: string;
  style?: Readonly<Record<string, string | number>>;
  [key: `data-${string}`]: boolean | string | undefined;
}

export function SidebarSectionHeaderMotion({
  isCollapsed,
  children,
  className,
  style,
  ...dataProps
}: SidebarSectionHeaderMotionProps) {
  const { resolvedMode } = useAnimation();

  const variants = useMemo((): Variants => {
    if (resolvedMode === "reduced") {
      return {
        collapsed: {
          opacity: 0,
          height: 0,
          marginBottom: 0,
          transition: { duration: 0.05 },
        },
        expanded: {
          opacity: 1,
          height: "auto",
          marginBottom: 4,
          transition: { duration: 0.1, delay: 0.05 },
        },
      };
    }

    const isDebug = resolvedMode === "debug";
    const enterSpring = isDebug
      ? debugSpringPresets.instant
      : springPresets.instant;
    const exitSpring = isDebug ? debugExitPresets.instant : exitPresets.instant;
    const delayMultiplier = isDebug ? 4 : 1;

    return {
      collapsed: {
        opacity: 0,
        height: 0,
        marginBottom: 0,
        scale: 0.96,
        transition: {
          opacity: { duration: 0.06 * delayMultiplier },
          height: { ...exitSpring },
          marginBottom: { ...exitSpring },
          scale: { duration: 0.06 * delayMultiplier },
        },
      },
      expanded: {
        opacity: 1,
        height: "auto",
        marginBottom: 4,
        scale: 1,
        transition: {
          height: { ...enterSpring },
          marginBottom: { ...enterSpring },
          opacity: {
            duration: 0.12 * delayMultiplier,
            delay: 0.03 * delayMultiplier,
          },
          scale: { ...enterSpring, delay: 0.03 * delayMultiplier },
        },
      },
    };
  }, [resolvedMode]);

  return (
    <motion.div
      {...dataProps}
      animate={isCollapsed ? "collapsed" : "expanded"}
      className={className}
      initial={false}
      style={{ ...style, overflow: "hidden", transformOrigin: "top" }}
      variants={variants}
    >
      {children}
    </motion.div>
  );
}
